import { useCallback, useMemo } from 'react';
import { useMedia } from 'react-use';

import { PillSkeleton } from '@/components/Skeleton';
import { SelectTokenFilter } from '@/components/TransactionModule/types';

import {
  DAI_ADDRESSES,
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USDC_ADDRESSES,
  USDT_ADDRESSES,
  WBTC_ADDRESSES,
  WRAPPED_NATIVE_ADDRESSES,
  ADDITIONAL_SUGGESTED_ADDRESSES,
  EXCLUDED_SUGGESTED_ADDRESSES,
} from '@/constants/addresses';
import { solana } from '@/constants/chain';

import { useTokenResults } from '@/hooks/tokenQueries/useTokenResults';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { varMappings } from '@/utils/generated/varMappings';
import { tokenProminentColorButtonStylesByThemeHSL } from '@/utils/tokenProminentColorButtonStyles';

import { PreviewToken } from '../../TokenPreviewPopover';
import { popularSolanaTokens } from './popular-solana-tokens';

const FALLBACK_TOKEN_BUTTON_STYLES = {
  light: {
    button: { background: varMappings['token-gray-200'] },
    buttonHover: { background: varMappings['token-gray-300'] },
    label: { color: varMappings['token-gray-1600'] },
  },
  dark: {
    button: { background: varMappings['token-gray-200'] },
    buttonHover: { background: varMappings['token-gray-300'] },
    label: { color: varMappings['token-gray-1600'] },
  },
};

const getTokenAddresses = (chainId: number) =>
  [
    NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId],
    WRAPPED_NATIVE_ADDRESSES[chainId],
    USDT_ADDRESSES[chainId],
    USDC_ADDRESSES[chainId],
    DAI_ADDRESSES[chainId],
    WBTC_ADDRESSES[chainId],
    ...(ADDITIONAL_SUGGESTED_ADDRESSES[chainId] || []),
  ].filter(
    (address) => !!address && !(EXCLUDED_SUGGESTED_ADDRESSES[chainId] ?? []).includes(address),
  );

const TokenPillButton = ({
  token,
  onClick,
  chainId,
}: {
  token: TokenResult;
  onClick(token: TokenResult): void;
  chainId: number;
}) => {
  const ldButtonStyles = useMemo(
    () =>
      token?.prominentColor
        ? tokenProminentColorButtonStylesByThemeHSL(token.prominentColor)
        : FALLBACK_TOKEN_BUTTON_STYLES,
    [token],
  );

  const vars = {} as Record<string, string>;
  const txtVars = {} as Record<string, string>;

  if (ldButtonStyles.light) {
    vars['--light-bg'] = String(ldButtonStyles.light.button.background);
    vars['--light-bg-hover'] = String(ldButtonStyles.light.buttonHover.background);
    txtVars['--light-txt'] = String(ldButtonStyles.light.label.color);
  }
  if (ldButtonStyles.dark) {
    vars['--dark-bg'] = String(ldButtonStyles.dark.button.background);
    vars['--dark-bg-hover'] = String(ldButtonStyles.dark.buttonHover.background);
    txtVars['--dark-txt'] = String(ldButtonStyles.dark.label.color);
  }
  if (!ldButtonStyles.dark && !ldButtonStyles.light) {
    vars['--light-bg'] = String(varMappings['token-gray-200']);
    vars['--light-bg-hover'] = String(varMappings['token-gray-300']);
    txtVars['--light-txt'] = String(varMappings['token-gray-1600']);
  }

  return (
    <button
      className={buttons({
        size: 'small',
        shape: 'pill',
        color: 'gray',
        class: [
          'pl-1.5 shrink-0 cursor-pointer',
          vars['--light-bg'] ? 'bg-[var(--light-bg)]' : 'bg-token-gray-200',
          vars['--light-bg-hover']
            ? 'hover:bg-[var(--light-bg-hover)] active:bg-[var(--light-bg-hover)]'
            : 'hover:bg-token-gray-300 active:bg-token-gray-300',
          vars['--dark-bg'] ? 'dark:bg-[var(--dark-bg)]' : 'dark:bg-token-gray-200',
          vars['--dark-bg-hover']
            ? 'dark:hover:bg-[var(--dark-bg-hover)] dark:active:bg-[var(--dark-bg-hover)]'
            : 'dark:hover:bg-token-gray-300 dark:active:bg-token-gray-300',
        ],
      })}
      onClick={() => onClick(token)}
      aria-label={`${token.name} token`}
      style={vars}
    >
      <TokenAvatar
        address={token.address}
        chainId={chainId}
        launchpad={token.launchpad?.name}
        size={24}
      />
      <span
        className={classNames(
          'ml-2',
          txtVars['--light-txt'] ? 'text-[var(--light-txt)]' : 'text-token-gray-1600',
          txtVars['--dark-txt'] ? 'dark:text-[var(--dark-txt)]' : 'dark:text-token-gray-1600',
        )}
        style={txtVars}
      >
        {token.symbol}
      </span>
    </button>
  );
};

export const TokenSuggestions = ({
  chainId,
  onClick,
  setPreviewTokenWithDelay,
  filterToken,
}: {
  chainId: number;
  onClick(token: TokenResult): void;
  setPreviewTokenWithDelay: (token: PreviewToken | null) => void;
  filterToken?: SelectTokenFilter;
}) => {
  const isMobile = useMedia(breakpoints.mobile, false);
  const tokenAddresses = useMemo(() => getTokenAddresses(chainId), [chainId]);
  const { tokenMap } = useTokenResults(tokenAddresses, chainId);

  const handleTokenPillClick = useCallback(
    (token: TokenResult) => {
      onClick(token);
    },
    [onClick],
  );

  const tokenPillsData = useMemo(() => {
    if (tokenMap === undefined) return [];
    // Return hardcoded popular tokens for Solana because useTokenResults doesn't support Solana.
    // Also, the addresses that are passed into useTokenResults are hardcoded anyways.
    const baseTokens =
      chainId === solana.id
        ? popularSolanaTokens
        : tokenMap[chainId]
          ? Object.values(tokenMap[chainId]).filter((token) => !!token)
          : [];

    // Drop pills for tokens the consumer has marked as non-selectable. Pills
    // are too compact to surface a disabled reason inline, so the chosen UX
    // is to omit them entirely rather than render a faded, unclickable pill.
    if (!filterToken) return baseTokens;
    return baseTokens.filter((token) => {
      const result = filterToken({ address: token.address, chainId: token.chainId });
      return typeof result === 'boolean' ? result : result.selectable;
    });
  }, [chainId, tokenMap, filterToken]);

  return (
    <div onMouseEnter={() => setPreviewTokenWithDelay(null)}>
      <span
        className={fontStyles({
          variant: 'BODY_SM',
          class: 'text-token-gray-800 h-8 flex items-center px-2',
        })}
      >
        Most popular
      </span>
      <div
        className={
          isMobile
            ? 'flex gap-2 mb-2.5 mx-2 overflow-x-auto'
            : 'flex gap-2 mb-2.5 mx-2 flex-wrap overflow-hidden h-10'
        }
      >
        {!tokenPillsData.length
          ? Array.from({ length: 4 }).map((_, index) => (
              <PillSkeleton key={index} width={70} height={36} />
            ))
          : tokenPillsData.map((token) => (
              <TokenPillButton
                key={token.address}
                token={token}
                onClick={handleTokenPillClick}
                chainId={chainId}
              />
            ))}
      </div>
    </div>
  );
};
